import React, { Suspense, lazy, useEffect } from "react";
import { useAuth } from "@content-buddy/shared";
import { BrowserRouter, useLocation } from "react-router";
import { ErrorBoundary } from "react-error-boundary";

import { beginLogin, isLoggingOut } from "./hooks/oidc/client";
import AuthErrorBoundary from "./AuthErrorBoundary";
import PlainSpinner from "./PlainSpinner";

// The Mantine/AppShell/query/websocket half of the app (Sentry included —
// see MantineRoot.tsx). Split into its own chunk so an unauthenticated visit
// to a protected route never has to fetch it — see
// docs/specifications/defer-mantine-app-bootstrap. Trade-off: Sentry does
// not observe errors on the unauthenticated bootstrap/redirect path.
const MantineRoot = lazy(() => import("./MantineRoot"));

// Routes that render without an established session: the OIDC redirect
// handlers (callback/initiate/logout/signed-out). These still use Mantine
// components, so they go through MantineRoot too, just lazily.
function isPublicAuthPath(pathname: string): boolean {
  return pathname.startsWith("/auth/");
}

function BootstrapGate(): React.ReactElement {
  const { pathname } = useLocation();
  const { isAuthenticated } = useAuth();
  const publicPath = isPublicAuthPath(pathname);

  useEffect(() => {
    // Skip while a logout is in flight: clearing tokens re-renders this
    // component with isAuthenticated=false before the browser has actually
    // navigated to /end_session, and firing beginLogin() here would race
    // logout()'s own redirect.
    if (!publicPath && !isAuthenticated && !isLoggingOut()) {
      void beginLogin(pathname);
    }
  }, [publicPath, isAuthenticated, pathname]);

  if (!publicPath && !isAuthenticated) {
    return <PlainSpinner />;
  }

  return (
    <Suspense fallback={<PlainSpinner />}>
      <MantineRoot />
    </Suspense>
  );
}

export default function App(): React.ReactElement {
  return (
    <ErrorBoundary FallbackComponent={AuthErrorBoundary}>
      <BrowserRouter>
        <BootstrapGate />
      </BrowserRouter>
    </ErrorBoundary>
  );
}
